import { rem } from 'polished'
import { memo } from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import { pipeSeparatorMessage } from 'LuxKit/intl/separators'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Image from 'components/Common/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferDescription from 'components/OfferPage/OfferDescription'
import OfferPromotions from 'components/OfferPage/OfferPromotions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'

import LoadingBox from 'components/Common/Loading/LoadingBox'
import OfferPageReviewAiSummary from 'components/OfferPage/NavigableOfferContent/OfferPageReviewAiSummary'
import useUserReviewsSummary from 'hooks/Reviews/useUserReviewsSummary'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'

import PropertyPopularFacilities from './PropertyPopularFacilities'
import PropertyTierProgressionPanel from 'loyalty/components/Banners/PropertyTierProgressionPanel'

const OfferIntroSection = styled(BodyText)`
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  promotions: Array<App.OfferPackageOptionView>
  propertyRates: Array<App.PropertyRate>
  duration: number
  checkoutDate?: string
  isUltraLux: boolean
  showTasteOfGoldPromo?: boolean
}

function PropertyPropertyDetails(props: Props) {
  const intl = useIntl()
  const {
    property,
    offer,
    promotions,
    propertyRates,
    duration,
    checkoutDate,
    isUltraLux,
    showTasteOfGoldPromo,
  } = props

  const location = property.location.title
  const sep = intl.formatMessage(pipeSeparatorMessage)
  const propertyLogoAlt = property.name
    ? intl.formatMessage({
        id: 'ybBusjTXR815C1wlVUim',
        defaultMessage: '{propertyName} logo',
        description: 'Alt text for a property logo image on the property details section of the offer page',
      }, { propertyName: property.name })
    : intl.formatMessage({
        id: 'Cr9ql3GjSlaTtl0HYYDd',
        defaultMessage: 'Vendor logo',
        description: 'Fallback alt text for a property logo image on the property details section of the offer page',
      })
  const { isAnyVariant: isHighlightReviewEnabled } = useExperiment(Experiments.croHighlightReviewAiSummary)
  const [reviewSummary, fetchingReviewSummary] = useUserReviewsSummary(
    property.id,
    'luxuryescapes',
  )
  const hasReviewSummaryContent = !!reviewSummary?.summary
  const showReviewSummary = isHighlightReviewEnabled && isOfferRatingDisplayable(property.rating)

  return (<>
    <HeaderSection>
      <Header>
        <VerticalSpacer gap={8}>
          <BodyTextBlock
            variant="small"
            data-testid="offer-location"
            startIcon={<LineMapMarkerIcon />}
          >
            {location && <FormattedMessage
              id="v_C61pq0a6_XdROVcmUY"
              defaultMessage="{location} {sep} <link>View on map</link>"
              description="Property location with link to view on map on the map view"
              values={{
                location,
                sep,
                link: (chunks) => <TextLink href="#getting-there">{chunks}</TextLink>,
              }}
            />}
            {!location && <TextLink href="#getting-there">{intl.formatMessage({
              id: 'AJC-wQ16MbO4N_EBndfu',
              defaultMessage: 'View on map',
              description: 'Link to view property location on map when no address is available',
            })}
            </TextLink>}
          </BodyTextBlock>
          <Heading variant="heading2" as="h1">{property.name}</Heading>
          <BodyText variant="large" as="h2">{property.headline}</BodyText>

          {!(showReviewSummary && hasReviewSummaryContent) && isOfferRatingDisplayable(property.rating) && <OfferRating
            link
            inlineLabel
            variant="small"
            rating={property.rating}
          />}
        </VerticalSpacer>
        {property.logo?.id &&
          <PropertyLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={property.logo.id}
            alt={propertyLogoAlt}
          />}
      </Header>
      {fetchingReviewSummary && showReviewSummary && <LoadingBox height={160} />}
      {reviewSummary?.summary && showReviewSummary && <OfferPageReviewAiSummary
        content={reviewSummary.summary}
        score={property.rating!.score}
        reviewsTotal={property.rating!.reviewsTotal}
      />}
      <PropertyTierProgressionPanel
        property={property}
        rates={propertyRates}
        nights={duration}
        checkoutDate={checkoutDate}
        isUltraLux={isUltraLux}
      />
      {showTasteOfGoldPromo && <TasteOfGoldPromoCard>
        <CSSBreakpoint max="mobile">
          <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
        </CSSBreakpoint>
      </TasteOfGoldPromoCard>}
    </HeaderSection>
    {offer.badge && <OfferBadgeLabel badge={offer.badge} />}

    <OfferIntroSection variant="large">
      {property.description && <OfferDescription description={property.description} />}
      {property.additionalDescription && <MarkdownRender type="compact" fontSize="medium" content={property.additionalDescription} />}
      {property.popularFacilities.length > 0 && <PropertyPopularFacilities popularFacilities={property.popularFacilities} />}
      {promotions.length > 0 && <OfferPromotions offer={offer} promotions={promotions} />}
    </OfferIntroSection>
  </>)
}

export default memo(PropertyPropertyDetails)
